/**
 * MultiSelect Component
 *
 * Generic multi-select dropdown for string arrays (tags, types, etc.)
 * Simplified version of UserMultiSelectDropdown for basic string options.
 */

'use client';

import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { tokens } from '@/lib/design-tokens';

interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  options: {
    label: string;
    value: string;
  }[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  maxDisplay?: number;
  className?: string;
  emptyMessage?: string;
}

export function MultiSelectInput({
  placeholder = 'Select options...',
  options,
  selectedValues,
  onSelectionChange,
  maxDisplay = 2,
  className,
  emptyMessage = 'No options found'
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (value: string) => {
    if (selectedValues.includes(value)) {
      // Remove value
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      // Add value
      onSelectionChange([...selectedValues, value]);
    }
  };

  const _removeValue = (value: string) => {
    onSelectionChange(selectedValues.filter((v) => v !== value));
  };

  const clearAll = () => {
    onSelectionChange([]);
    setSearchTerm('');
  };

  // Display selected values as badges
  const _displayedValues = selectedValues.slice(0, maxDisplay);
  const _remainingCount = selectedValues.length - maxDisplay;

  return (
    <div className={cn('inline-block', className)}>
      {/* Minimal pill-style dropdown */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            size="sm"
            className={cn(
              'rounded-base border-border !text-muted-foreground h-8 w-full bg-[var(--input-bg)] px-3 text-sm font-normal'
            )}
          >
            {selectedValues.length > 0 ? (
              <span className="grow text-left">
                {placeholder} ({selectedValues.length})
              </span>
            ) : (
              <span className="grow text-left">{placeholder}</span>
            )}

            <ChevronDown
              className={cn(tokens.icon(), !options.length && 'opacity-50')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);

                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="flex-1">{option.label}</span>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
                {selectedValues.length > 0 && (
                  <CommandItem
                    value="clear-all"
                    onSelect={clearAll}
                    className="text-muted-foreground"
                  >
                    <span className="text-sm">Clear all selections</span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
